import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { KeyValuePriority } from "../../../types/keyValue";

import { useUserContext } from "../../../contexts/userContext";

import { addReport, getReportCategory } from "../../../services/reportServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonButton from "../../../components/Button";
import CommonInput from "../../../components/Input/CommonInput";
import CommonRadio from "../../../components/Input/Radio";
import StickyHeader from "../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import SimplePopup from "../../../components/Popup/Simple";
import BottomButtonGroup from "../../../components/Button/Bottom";

type Props = { type: "comment" | "post" };

export default function TalkReport({ type }: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [categoryList, setCategoryList] = useState<KeyValuePriority[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popup, setPopup] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const location = useLocation();

  const validate = (error?: boolean) => {
    if (selectedCategory === "") {
      if (error) {
        setErrorMessage("신고 사유를 선택해주세요.");
        setPopup("error");
      }
      return false;
    }

    if (selectedCategory === "OTHER" && content === "") {
      if (error) {
        setErrorMessage("신고 사유를 입력해주세요.");
        setPopup("error");
      }
      return false;
    }

    return true;
  };

  const handleReport = () => {
    if (validate(true) === false) return;

    setIsLoading(true);
    addReport({
      [type === "post" ? "lifePostId" : "lifeCommentId"]: Number(id),
      createdBy: user.memberId ?? 1,
      reportCategory: selectedCategory,
      content: content,
    })
      .then(
        (res) => {
          if (res.status === HttpStatusCode.Ok) {
            setPopup("success");
          } else {
            setPopup("fail");
          }
        },
        (rej) => {
          if (rej.response?.data?.message) {
            setErrorMessage(rej.response.data.error.split("-").pop().trim());
            setPopup("error");
          } else {
            setPopup("fail");
          }
        }
      )
      .finally(() => setIsLoading(false));
  };

  const loadReportCategory = () => {
    setIsLoading(true);
    getReportCategory()
      .then((res) => {
        const tempCategoryList: KeyValuePriority[] = [];
        Object.entries(res.data).forEach(([key, value]) => {
          const priority = key === "OTHER" ? 0 : 1;
          tempCategoryList.push({ key, value, priority });
        });

        setCategoryList(
          tempCategoryList.sort((a, b) => {
            return b.priority - a.priority;
          })
        );
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadReportCategory();
  }, []);

  return (
    <div className="z-40 bg-white flex flex-col gap-20">
      {loadingComponent}
      <div className="flex flex-col">
        <StickyHeader
          title="신고하기"
          onBackClick={() => navigate("/view")}
          className="px-5"
        />
        <div className="px-5">
          <div className="text-sm text-brand-neutral-60 py-2.5 inline-flex">
            신고 사유 선택<p className="text-red-600 m-0">*</p>
          </div>
          {categoryList.map((item, index) => {
            return (
              <div className="flex flex-col" key={index}>
                {index !== 0 && <HorizontalRule margin="0" />}
                <div
                  className="flex gap-3 text-base font-normal leading-[26px] items-center"
                  onClick={() => setSelectedCategory(item.key)}
                >
                  <CommonRadio
                    value={selectedCategory === item.key}
                    className="py-4"
                  />
                  {item.value}
                </div>
              </div>
            );
          })}
          <CommonInput
            placeholder="신고내용을 입력해 주세요"
            disabled={selectedCategory !== "OTHER"}
            value={content ?? ""}
            onChange={(value) => setContent(value)}
          />
        </div>
      </div>
      <div className="flex flex-col px-5">
        <div className="flex flex-col gap-2.5 items-center px-2.5 py-5 bg-[#F7F7F7]">
          <div className="text-sm font-normal text-brand-neutral-80 whitespace-pre-line text-center leading-6">
            {
              "반복 신고 하여도 조치되지 않을 경우\n카카오톡 채널로 문의해주세요"
            }
          </div>
          <a href="https://pf.kakao.com/_xhJxkZK/chat">
            <CommonButton
              variant="transparent"
              className="border border-gray-20 border-opacity-20 rounded-[3px] bg-brand-kakao-yellow text-black text-sm font-medium leading-[17px] px-4 py-[11px] gap-2"
            >
              <KakaoTalkIcon />
              카카오톡 문의
            </CommonButton>
          </a>
        </div>
      </div>
      <BottomButtonGroup
        validation={!validate()}
        onClick={handleReport}
        title="샌디에게 보내기"
      />

      <SimplePopup
        isOpen={popup === "success"}
        message="신고가 접수되었습니다."
        onClose={() => {
          setPopup("");
          navigate(`/talk/${type === "post" ? id : location?.state?.postId}`);
        }}
      />
      <SimplePopup
        isOpen={popup === "fail"}
        message="신고 접수에 실패하였습니다."
        onClose={() => {
          setPopup("");
        }}
      />
      <SimplePopup
        isOpen={popup === "error"}
        message={errorMessage}
        onClose={() => {
          setPopup("");
        }}
      />
    </div>
  );
}

export const KakaoTalkIcon = () => (
  <svg
    width="27"
    height="26"
    viewBox="0 0 27 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.4907 1C6.29943 1 0.5 5.63949 0.5 11.2719C0.5 14.9279 2.91256 18.1291 6.53141 19.9571L5.30657 24.5318C5.28347 24.6003 5.27992 24.6739 5.29631 24.7444C5.3127 24.8148 5.34838 24.8794 5.39936 24.9307C5.47369 24.9962 5.56934 25.0325 5.66845 25.0327C5.75063 25.0261 5.8286 24.9937 5.89115 24.94L11.1617 21.3861C11.9397 21.4935 12.7239 21.5492 13.5093 21.5531C20.6913 21.5531 26.5 16.9136 26.5 11.2719C26.5 5.63022 20.6727 1 13.4907 1Z"
      fill="#392020"
    />
    <path
      d="M5.04725 9.64779H3.62755C3.45158 9.6529 3.2778 9.60783 3.12648 9.51787C3.05929 9.47578 3.00154 9.42023 2.95683 9.35476C2.91212 9.2893 2.88142 9.21529 2.86666 9.1374C2.86215 9.08801 2.86215 9.0384 2.86666 8.98901C2.86225 8.89661 2.88019 8.80445 2.91896 8.72045C2.95773 8.63645 3.01618 8.56308 3.08936 8.50648C3.25113 8.38933 3.44638 8.32744 3.6461 8.33012H7.92376C8.10112 8.32389 8.2761 8.37252 8.42483 8.46933C8.4936 8.50956 8.55258 8.56461 8.59751 8.6304C8.64244 8.69619 8.6722 8.77111 8.68465 8.84981C8.68918 8.89609 8.68918 8.94273 8.68465 8.98901C8.68902 9.08275 8.67112 9.17617 8.63241 9.26166C8.59371 9.34714 8.53529 9.42217 8.46195 9.48072C8.30412 9.59774 8.11075 9.65678 7.91448 9.64779H6.54118V14.5286C6.54676 14.6326 6.53066 14.7365 6.49393 14.834C6.45719 14.9314 6.4006 15.0202 6.32776 15.0946C6.25791 15.1658 6.17394 15.2215 6.08125 15.2583C5.98856 15.295 5.8892 15.312 5.78957 15.308C5.6153 15.313 5.44474 15.2573 5.30706 15.1503C5.17599 15.0459 5.08703 14.8975 5.05653 14.7327C5.04736 14.665 5.04736 14.5963 5.05653 14.5286L5.04725 9.64779Z"
      fill="#FEE500"
    />
    <path
      d="M9.09291 8.69213C9.13372 8.53943 9.22957 8.40721 9.362 8.32094C9.50312 8.24096 9.66391 8.20237 9.82596 8.2096H10.1786C10.3505 8.20343 10.5208 8.2452 10.6704 8.33023C10.8289 8.43682 10.9415 8.59911 10.9858 8.7849L12.8417 14.0276C12.8898 14.1644 12.93 14.3039 12.9623 14.4452C12.9655 14.504 12.9655 14.5628 12.9623 14.6215C12.9654 14.7119 12.9488 14.8019 12.9135 14.8853C12.8783 14.9686 12.8252 15.0433 12.7581 15.104C12.6931 15.1706 12.6151 15.2232 12.5289 15.2583C12.4427 15.2934 12.3501 15.3104 12.2571 15.3081C12.114 15.3224 11.9702 15.288 11.849 15.2106C11.7278 15.1332 11.6362 15.0172 11.589 14.8813L11.1993 13.74H8.75886L8.36914 14.8813C8.32305 15.0198 8.23034 15.1381 8.10682 15.2159C7.9833 15.2938 7.83662 15.3263 7.69176 15.3081C7.53486 15.3122 7.38116 15.2632 7.25565 15.1689C7.13336 15.0717 7.05085 14.9331 7.02367 14.7793C7.01875 14.7268 7.01875 14.6739 7.02367 14.6215C7.00774 14.5294 7.00774 14.4353 7.02367 14.3432C7.02367 14.2411 7.08862 14.1297 7.12574 14.0276L9.09291 8.69213ZM10.0208 9.90774L9.1393 12.6915H10.893L10.0208 9.90774Z"
      fill="#FEE500"
    />
    <path
      d="M13.1746 8.98904C13.1652 8.7808 13.2386 8.57731 13.3788 8.42303C13.45 8.35115 13.5356 8.29498 13.6299 8.25822C13.7242 8.22146 13.8251 8.20483 13.9262 8.20951C14.1001 8.20748 14.2697 8.26298 14.4087 8.3673C14.5376 8.47614 14.6232 8.62755 14.65 8.7941C14.6591 8.85873 14.6591 8.92441 14.65 8.98904V13.8698H17.2018C17.3786 13.8675 17.5525 13.9157 17.7028 14.0089C17.7704 14.0506 17.8284 14.1061 17.8732 14.1716C17.918 14.2371 17.9484 14.3113 17.9626 14.3894C17.9626 14.3894 17.9626 14.4822 17.9626 14.5286C17.9671 14.621 17.9491 14.7131 17.9103 14.7971C17.8716 14.8811 17.8131 14.9546 17.7399 15.0112C17.5782 15.1283 17.3829 15.1901 17.1832 15.1874H14.0469C13.8471 15.2003 13.6486 15.1483 13.4808 15.039C13.3308 14.9268 13.2309 14.7602 13.2025 14.5751C13.1957 14.4731 13.1957 14.3707 13.2025 14.2688L13.1746 8.98904Z"
      fill="#FEE500"
    />
    <path
      d="M18.1309 8.98936C18.1254 8.78172 18.1982 8.57963 18.335 8.42334C18.4741 8.29697 18.6525 8.22235 18.8401 8.21221C19.0278 8.20207 19.2132 8.25697 19.365 8.36762C19.4967 8.4755 19.5856 8.62683 19.6156 8.79442C19.6201 8.85929 19.6201 8.92448 19.6156 8.98936V11.2627L21.9632 8.55315C22.0536 8.46072 22.1497 8.37403 22.2508 8.29342C22.3461 8.23667 22.4554 8.20766 22.5663 8.20983C22.7285 8.20894 22.8871 8.25752 23.021 8.34904C23.0841 8.39152 23.138 8.44634 23.1794 8.51011C23.2209 8.57388 23.249 8.64532 23.2622 8.72023C23.266 8.73859 23.266 8.75759 23.2622 8.77595C23.2577 8.79737 23.2577 8.81944 23.2622 8.84086C23.2619 8.9385 23.2364 9.03444 23.188 9.11928C23.1388 9.2091 23.0796 9.29304 23.0117 9.36972L21.4714 11.0586L23.3272 13.9351V14.0278C23.4236 14.1608 23.4987 14.308 23.5499 14.464V14.5104C23.5606 14.6179 23.5451 14.7264 23.5047 14.8266C23.4643 14.9268 23.4002 15.0157 23.3179 15.0857C23.1631 15.2027 22.9737 15.2648 22.7797 15.262C22.6508 15.2673 22.5228 15.2384 22.4086 15.1784C22.285 15.0956 22.1831 14.9842 22.1116 14.8537L20.3857 12.07L19.5599 12.933V14.4825C19.5654 14.6901 19.4926 14.8922 19.3557 15.0485C19.2824 15.1202 19.1952 15.1762 19.0994 15.2129C19.0037 15.2496 18.9015 15.2663 18.799 15.262C18.6278 15.2656 18.4606 15.2098 18.3258 15.1042C18.1947 14.9998 18.1057 14.8515 18.0752 14.6867C18.0661 14.619 18.0661 14.5502 18.0752 14.4825L18.1309 8.98936Z"
      fill="#FEE500"
    />
  </svg>
);
